import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { ClubLogo } from "./ClubLogo";

const NAV_LINKS = [
  { label: "Home", path: "/", testId: "nav-link-home" },
  { label: "Teams", path: "/teams", testId: "nav-link-teams" },
  { label: "Fixtures", path: "/fixtures", testId: "nav-link-fixtures" },
  { label: "Events", path: "/events", testId: "nav-link-events" },
  { label: "Honours", path: "/honours", testId: "nav-link-honours" },
  { label: "Social & Bar", path: "/social", testId: "nav-link-social" },
  { label: "News", path: "/news", testId: "nav-link-news" },
  { label: "Gallery", path: "/gallery", testId: "nav-link-gallery" },
  { label: "Sponsorship", path: "/sponsorship", testId: "nav-link-sponsorship" },
  { label: "Booking", path: "/booking", testId: "nav-link-booking" },
  { label: "Contact", path: "/contact", testId: "nav-link-contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0B1520]/80 border-b border-white/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-brand-link" onClick={() => setOpen(false)}>
          <ClubLogo size={38} />
          <span className="font-head font-extrabold tracking-tight text-sm sm:text-base leading-tight">
            Whitley Bay
            <span className="block text-[10px] sm:text-xs font-mono-club uppercase tracking-[0.25em] text-[#E5AC3C]">
              Cricket Club
            </span>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-6" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              data-testid={l.testId}
              className={({ isActive }) =>
                `text-sm font-semibold transition-colors duration-200 hover:text-[#E5AC3C] ${
                  isActive ? "text-[#E5AC3C]" : "text-[#9BB0C1]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contact"
            data-testid="nav-cta-join-button"
            className="hidden sm:inline-flex bg-[#E5AC3C] hover:bg-[#F3C35B] text-[#0B1520] font-bold text-sm px-5 py-2.5 rounded-full transition-colors duration-200 shadow-[0_0_20px_rgba(229,172,60,0.25)]"
          >
            Join the Club
          </Link>
          <button
            className="xl:hidden text-[#F7F5F0] p-2"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-menu-button"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            key={location.pathname}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="xl:hidden overflow-hidden border-t border-white/10 bg-[#0B1520]/95"
            aria-label="Mobile"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.path}
                  to={l.path}
                  data-testid={`${l.testId}-mobile`}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `py-2.5 text-base font-semibold transition-colors hover:text-[#E5AC3C] ${
                      isActive ? "text-[#E5AC3C]" : "text-[#9BB0C1]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="nav-cta-join-button-mobile"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex justify-center bg-[#E5AC3C] hover:bg-[#F3C35B] text-[#0B1520] font-bold text-sm px-5 py-3 rounded-full transition-colors"
              >
                Join the Club
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
